import { Alert, Box, Card, CardContent, Container, Divider, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { humanise } from '@/components/common'

interface PublicTerms {
  companyName: string | null
  serviceType: string | null
  documentType: string | null
  clauses: string[]
}

/**
 * The terms behind the code printed on a document.
 *
 * <p>Reached by scanning, on a phone, standing at a counter - so it is a page
 * and nothing else: no sign-in, no menu, no application around it. Whoever holds
 * the paper is entitled to read what it was sold on.</p>
 *
 * <p>Read live rather than frozen into the square when the document was printed,
 * which is the whole reason to print a code instead of half a page of clauses.</p>
 */
export default function PublicTermsPage() {
  const [params] = useSearchParams()
  const [terms, setTerms] = useState<PublicTerms | null>(null)
  const [failed, setFailed] = useState(false)

  const company = params.get('c')
  const service = params.get('s')
  const document = params.get('d')

  useEffect(() => {
    if (!company) {
      setFailed(true)
      return
    }
    const query = new URLSearchParams({ c: company })
    if (service) query.set('s', service)
    if (document) query.set('d', document)
    fetch(`/api/v1/public/terms?${query.toString()}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then(setTerms)
      .catch(() => setFailed(true))
  }, [company, service, document])

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h6">{terms?.companyName ?? 'Terms and conditions'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[
              service && service !== 'ALL' ? humanise(service) : null,
              document && document !== 'ALL' ? humanise(document) : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'Terms and conditions'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {failed ? (
            <Alert severity="warning">
              These terms could not be read. Ask the branch that gave you the document.
            </Alert>
          ) : terms == null ? (
            <Typography variant="body2" color="text.secondary">
              Reading…
            </Typography>
          ) : terms.clauses.length === 0 ? (
            <Alert severity="info">Nothing is written for this document.</Alert>
          ) : (
            <Box component="ol" sx={{ pl: 3, m: 0 }}>
              {terms.clauses.map((clause, index) => (
                <Typography component="li" variant="body2" key={index} sx={{ mb: 1.25 }}>
                  {clause}
                </Typography>
              ))}
            </Box>
          )}

          {/* Said plainly: what is on the screen may have moved on from what is
              on the paper, and the screen is the one that stands. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            These are the terms as they stand today.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  )
}
